import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Card, CardContent, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

// The placeholder counters that used to live here were never rendered, and the
// numbers were not real. Each feature now carries a short, honest description
// instead, which is the only thing the card can actually stand behind.
const FEATURELIST = [
  {
    id: 'mentors',
    name: 'Professional Mentors',
    description: 'Learn from licensed practitioners who have sat the same exams.',
    icon: 'line-md:emoji-grin',
    color: '#1877F2',
  },
  {
    id: 'videos',
    name: 'Video Lessons',
    description: 'On-demand recorded sessions you can revisit at any time.',
    icon: 'line-md:play-twotone',
    color: '#DF3E30',
  },
  {
    id: 'questions',
    name: 'Previous Questions',
    description: 'A large bank of exam-style practice questions to work through.',
    icon: 'line-md:question-circle',
    color: '#006097',
  },
  {
    id: 'result',
    name: '100% Result Guaranteed',
    description: 'Exam-focused coaching with continuous assessment along the way.',
    icon: 'line-md:clipboard-check-twotone',
    color: '#1C9CEA',
  },
];

// ----------------------------------------------------------------------

FeatureWidget.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  sx: PropTypes.object,
};

export default function FeatureWidget({
  title = 'Why students choose ASAP',
  subheader = 'The support, material and mentoring that come with every ASAP programme.',
  sx,
  ...other
}) {
  return (
    <Card {...other} sx={{ borderRadius: 3, ...sx }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box sx={{ mb: { xs: 2.5, md: 3 }, maxWidth: 720 }}>
          <Typography component="h2" variant="h4" sx={{ color: 'text.primary', mb: subheader ? 1 : 0 }}>
            {title}
          </Typography>

          {subheader && (
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
              {subheader}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 2.5 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          }}
        >
          {FEATURELIST.map((feature, index) => (
            <Box
              component={motion.div}
              key={feature.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: Math.min(index, 4) * 0.08, ease: 'easeOut' }}
              sx={{ height: '100%' }}
            >
              {/* The lift lives on an inner box because framer-motion owns the
                  transform of the animated wrapper. */}
              <Box
                sx={{
                  height: '100%',
                  p: { xs: 2.25, md: 2.5 },
                  textAlign: 'center',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
                  '@media (hover: hover)': {
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: alpha(feature.color, 0.4),
                      boxShadow: (theme) => `0 14px 28px ${alpha(theme.palette.grey[900], 0.1)}`,
                    },
                    '&:hover .feature-icon': {
                      transform: 'scale(1.08)',
                      backgroundColor: alpha(feature.color, 0.18),
                    },
                  },
                }}
              >
                <Box
                  className="feature-icon"
                  sx={{
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    mb: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '50%',
                    color: feature.color,
                    backgroundColor: alpha(feature.color, 0.12),
                    transition: 'transform 0.3s ease, background-color 0.3s ease',
                  }}
                >
                  <Iconify icon={feature.icon} width={28} />
                </Box>

                <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 700, mb: 0.5 }}>
                  {feature.name}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  {feature.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
